import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { useCustomer } from "./CustomerContext";
import { useDataAccess } from "../hooks/useDataAccess";

export const KronkOrdersContext = createContext();

export const KronkOrdersProvider = ({ children }) => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const { getEffectiveCustomerId } = useCustomer();
  const { makeCustomerApiRequest } = useDataAccess();

  const fetchOrders = useCallback(async (forceRefresh = false) => {
    const customerId = getEffectiveCustomerId();
    
    // Only fetch for Kronk customer
    if (customerId !== 'kronk') {
      setOrders([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Build query parameters
      const params = new URLSearchParams();
      params.set('limit', '250');

      // Add cache-busting parameter when doing a forced refresh
      const endpoint = forceRefresh 
        ? `orders?${params.toString()}&_cache_buster=${Date.now()}`
        : `orders?${params.toString()}`;
        
      const response = await makeCustomerApiRequest(endpoint, {}, 'kronk');
      
      // Handle both response structures: { orders: [...] } or direct array
      let ordersArray = [];
      if (Array.isArray(response)) {
        ordersArray = response;
      } else if (response && response.orders && Array.isArray(response.orders)) {
        ordersArray = response.orders;
      } else if (response && Array.isArray(response.data)) {
        ordersArray = response.data;
      }
      
      setOrders(ordersArray);
    } catch (err) {
      console.error('KronkOrdersContext: Failed to load orders:', err);
      setError(err.message || 'Failed to load orders');
      setOrders([]);
    } finally {
      setLoading(false);
    }
  }, [getEffectiveCustomerId, makeCustomerApiRequest]);

  // Refresh function for manual refresh
  const refreshOrders = useCallback(() => {
    fetchOrders(true);
  }, [fetchOrders]);

  // Effect for the initial data load and when customer changes
  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  // Listen for global refresh events
  useEffect(() => {
    const handleGlobalRefresh = () => {
      refreshOrders();
    };

    window.addEventListener('globalRefresh', handleGlobalRefresh);
    return () => window.removeEventListener('globalRefresh', handleGlobalRefresh);
  }, [refreshOrders]);

  return (
    <KronkOrdersContext.Provider value={{ orders, loading, error, refreshOrders, fetchOrders }}>
      {children}
    </KronkOrdersContext.Provider>
  );
};

export const useKronkOrders = () => {
  const context = useContext(KronkOrdersContext);
  if (!context) {
    throw new Error('useKronkOrders must be used within a KronkOrdersProvider');
  }
  return context;
};

